import React from 'react';
import styled from 'styled-components';
import { tokens } from '../../design-system/tokens';
import { useNavigate } from 'react-router-dom';

const Container = styled.div`
  background-color: ${tokens.colors.neutral[0]};
  min-height: 100vh;
  padding: ${tokens.spacing[10]} ${tokens.spacing[5]};

  @media (max-width: ${tokens.breakpoints.md}) {
    padding: ${tokens.spacing[8]} ${tokens.spacing[4]};
  }
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.spacing[2]};
  padding: ${tokens.spacing[3]} ${tokens.spacing[5]};
  background-color: ${tokens.colors.neutral[100]};
  border: 1px solid ${tokens.colors.neutral[200]};
  border-radius: ${tokens.borderRadius.lg};
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[700]};
  cursor: pointer;
  transition: all ${tokens.animation.duration.fast} ${tokens.animation.easing.ease};
  margin-bottom: ${tokens.spacing[8]};

  &:hover {
    background-color: ${tokens.colors.neutral[200]};
  }
`;

const Title = styled.h1`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize['3xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[900]};
  margin-bottom: ${tokens.spacing[8]};
  word-break: keep-all;
`;

const Section = styled.section`
  margin-bottom: ${tokens.spacing[10]};
`;

const SectionTitle = styled.h2`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.xl};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
  margin-bottom: ${tokens.spacing[4]};
  word-break: keep-all;
`;

const Paragraph = styled.p`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.base};
  line-height: 1.8;
  color: ${tokens.colors.neutral[700]};
  margin-bottom: ${tokens.spacing[4]};
  word-break: keep-all;
`;

const List = styled.ol`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.base};
  line-height: 1.8;
  color: ${tokens.colors.neutral[700]};
  margin: ${tokens.spacing[4]} 0;
  padding-left: ${tokens.spacing[8]};

  li {
    margin-bottom: ${tokens.spacing[3]};
    word-break: keep-all;
  }
`;

const UpdateDate = styled.p`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[500]};
  margin-bottom: ${tokens.spacing[6]};
`;

export const TermsOfService: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container>
      <ContentWrapper>
        <BackButton onClick={() => navigate(-1)}>
          ← 뒤로 가기
        </BackButton>

        <Title>럭키온(LuckyOn) 이용약관</Title>
        <UpdateDate>시행일: 2025년 10월 8일</UpdateDate>

        <Section>
          <SectionTitle>제1조 (목적)</SectionTitle>
          <Paragraph>
            이 약관은 카인드바이럴(이하 "회사")이 운영하는 럭키온(LuckyOn) 서비스(이하 "서비스")의
            이용과 관련하여 회사와 이용자의 권리, 의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>제2조 (용어의 정의)</SectionTitle>
          <List>
            <li>"서비스"란 회사가 제공하는 AI 기반 운세 콘텐츠 서비스를 의미합니다.</li>
            <li>"이용자"란 회사의 서비스에 접속하여 이 약관에 따라 서비스를 이용하는 회원 및 비회원을 말합니다.</li>
            <li>"회원"이란 회사와 서비스 이용계약을 체결하고 회원 ID를 부여받은 이용자를 말합니다.</li>
            <li>"콘텐츠"란 서비스가 제공하는 오늘의 운세, 사주팔자, 토정비결, 궁합, 연애운, 재물운 등 모든 운세 정보를 의미합니다.</li>
            <li>"유료서비스"란 회사가 유료로 제공하는 각종 운세 콘텐츠 및 구독 서비스를 의미합니다.</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>제3조 (약관의 게시와 개정)</SectionTitle>
          <List>
            <li>회사는 이 약관의 내용을 이용자가 쉽게 알 수 있도록 서비스 초기 화면에 게시합니다.</li>
            <li>회사는 필요한 경우 관련 법령을 위배하지 않는 범위에서 이 약관을 개정할 수 있습니다.</li>
            <li>회사가 약관을 개정할 경우에는 적용일자 및 개정사유를 명시하여 현행약관과 함께 서비스 초기화면에 그 적용일자 7일 이전부터 적용일자 전일까지 공지합니다.</li>
            <li>회원이 개정약관의 적용에 동의하지 않는 경우 회사는 해당 회원에 대해 개정약관의 내용을 적용할 수 없으며, 이 경우 회원은 이용계약을 해지할 수 있습니다.</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>제4조 (서비스의 제공 및 변경)</SectionTitle>
          <List>
            <li>회사는 다음과 같은 서비스를 제공합니다:
              <List style={{ listStyleType: 'disc', marginTop: tokens.spacing[2] }}>
                <li>오늘의 운세</li>
                <li>사주팔자 분석</li>
                <li>토정비결</li>
                <li>궁합 분석</li>
                <li>연애운 분석</li>
                <li>재물운 분석</li>
                <li>기타 회사가 정하는 운세 콘텐츠</li>
              </List>
            </li>
            <li>서비스는 연중무휴, 1일 24시간 제공함을 원칙으로 합니다.</li>
            <li>회사는 컴퓨터 등 정보통신설비의 보수점검, 교체 및 고장, 통신두절 또는 운영상 상당한 이유가 있는 경우 서비스의 제공을 일시적으로 중단할 수 있습니다.</li>
            <li>회사는 서비스의 제공에 필요한 경우 정기점검을 실시할 수 있으며, 정기점검시간은 서비스 제공화면에 공지한 바에 따릅니다.</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>제5조 (회원가입)</SectionTitle>
          <List>
            <li>이용자는 회사가 정한 가입 양식에 따라 회원정보를 기입한 후 이 약관에 동의한다는 의사표시를 함으로써 회원가입을 신청합니다.</li>
            <li>회사는 다음 각 호에 해당하지 않는 한 회원으로 등록합니다:
              <List style={{ listStyleType: 'disc', marginTop: tokens.spacing[2] }}>
                <li>가입신청자가 이 약관에 의하여 이전에 회원자격을 상실한 적이 있는 경우</li>
                <li>등록 내용에 허위, 기재누락, 오기가 있는 경우</li>
                <li>기타 회원으로 등록하는 것이 회사의 기술상 현저히 지장이 있다고 판단되는 경우</li>
              </List>
            </li>
            <li>회원가입계약의 성립시기는 회사의 승낙이 회원에게 도달한 시점으로 합니다.</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>제6조 (회원정보의 변경)</SectionTitle>
          <List>
            <li>회원은 개인정보관리화면을 통하여 언제든지 본인의 개인정보를 열람하고 수정할 수 있습니다.</li>
            <li>회원은 회원가입신청 시 기재한 사항이 변경되었을 경우 온라인으로 수정을 하거나 이메일 등의 방법으로 회사에 대하여 그 변경사항을 알려야 합니다.</li>
            <li>회원이 전항의 변경사항을 회사에 알리지 않아 발생한 불이익에 대하여 회사는 책임지지 않습니다.</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>제7조 (유료서비스 이용)</SectionTitle>
          <List>
            <li>회사가 제공하는 유료서비스를 이용하려는 회원은 정해진 요금을 지불하고 이용할 수 있습니다.</li>
            <li>회사는 유료서비스 이용요금을 회사와 계약한 전자지불업체에서 정한 방법에 의하거나 회사가 정한 청구서에 합산하여 청구할 수 있습니다.</li>
            <li>유료서비스 이용을 위해 결제한 금액에 대한 환불은 결제일로부터 7일 이내에 서비스를 이용하지 않은 경우에 한하여 전액 환불이 가능합니다.</li>
            <li>서비스를 이용한 경우 환불이 불가능하며, 이는 디지털 콘텐츠의 특성상 서비스 제공이 완료된 것으로 간주되기 때문입니다.</li>
            <li>구독 서비스는 구독 기간 만료 시 자동으로 종료되며, 자동 갱신되지 않습니다.</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>제8조 (저작권의 귀속)</SectionTitle>
          <List>
            <li>서비스에서 제공되는 모든 콘텐츠의 저작권 및 지적재산권은 회사에 귀속됩니다.</li>
            <li>이용자는 서비스를 이용함으로써 얻은 정보 중 회사에 지적재산권이 귀속된 정보를 회사의 사전 승낙 없이 복제, 송신, 출판, 배포, 방송 기타 방법에 의하여 영리목적으로 이용하거나 제3자에게 이용하게 하여서는 안됩니다.</li>
            <li>서비스에서 제공하는 운세 콘텐츠는 재미와 오락을 위한 것으로, 실제 미래를 예측하거나 보장하는 것이 아님을 명확히 합니다.</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>제9조 (서비스 이용의 제한 및 중지)</SectionTitle>
          <Paragraph>
            회사는 회원이 다음 각 호에 해당하는 경우 사전통지 없이 서비스 이용을 제한하거나 중지할 수 있습니다:
          </Paragraph>
          <List>
            <li>타인의 정보를 도용한 경우</li>
            <li>서비스 운영을 고의로 방해한 경우</li>
            <li>허위 정보를 기재한 경우</li>
            <li>기타 관련 법령이나 회사가 정한 이용조건에 위배되는 경우</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>제10조 (면책조항)</SectionTitle>
          <List>
            <li>회사는 천재지변, 전쟁, 테러, 해킹, DDoS 공격 등 불가항력으로 인하여 서비스를 제공할 수 없는 경우에는 서비스 제공에 관한 책임이 면제됩니다.</li>
            <li>회사는 이용자의 귀책사유로 인한 서비스 이용의 장애에 대하여는 책임을 지지 않습니다.</li>
            <li>회사는 이용자가 서비스와 관련하여 게재한 정보, 자료, 사실의 신뢰도, 정확성 등의 내용에 관하여는 책임을 지지 않습니다.</li>
            <li>회사가 제공하는 운세 콘텐츠는 오락 목적으로 제공되며, 이를 근거로 한 의사결정에 대한 책임은 이용자 본인에게 있습니다.</li>
            <li>회사는 서비스 이용과 관련하여 이용자에게 발생한 손해 중 이용자의 고의, 과실에 의한 손해에 대하여 책임을 지지 않습니다.</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>제11조 (분쟁해결)</SectionTitle>
          <List>
            <li>회사와 이용자 간에 발생한 분쟁은 전자거래기본법 제28조 및 동 시행령 제15조에 의하여 설치된 전자거래분쟁조정위원회의 조정에 따를 수 있습니다.</li>
            <li>회사와 이용자 간에 제기된 소송은 대한민국법을 준거법으로 하며, 민사소송법상의 관할법원에 제기합니다.</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>제12조 (기타)</SectionTitle>
          <Paragraph>
            이 약관에서 정하지 아니한 사항과 이 약관의 해석에 관하여는 전자거래기본법, 전자서명법,
            정보통신망 이용촉진 및 정보보호 등에 관한 법률, 전자상거래 등에서의 소비자보호에 관한 법률 등 관련법령 또는 상관례에 따릅니다.
          </Paragraph>
        </Section>

        <Section>
          <Paragraph style={{ fontWeight: tokens.typography.fontWeight.semibold }}>
            부칙
          </Paragraph>
          <Paragraph>
            이 약관은 2025년 10월 8일부터 시행합니다.
          </Paragraph>
        </Section>
      </ContentWrapper>
    </Container>
  );
};